import React from 'react';
import { Button, Checkbox, Form, Input, Modal, Spin, message } from 'antd';
import { TPayerIdEntry, TPracticeProvider } from 'types/insuranceEligibility';
import { TInsuranceServer } from 'types/common';
import { getPayerIdSetup, saveEligibilitySetup, savePayerEntries } from 'api/eligibility';
import { getAllInsuranceList } from 'api/common';
import { lookupNpiStandalone } from 'api/insuranceBilling';
import HiddenFormField from 'pages/InsuranceBillingOnboarding/components/HiddenFormField';
import InfoTip from 'pages/InsuranceBillingOnboarding/components/InfoTip';
import NpiRegistryBox from 'pages/InsuranceBillingOnboarding/components/NpiRegistryBox';
import { NPI_MODAL_COPY as C } from '../copy';
import s from '../s.module.less';

const InfoIcon: React.FC = () => (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <circle cx="10" cy="10" r="8" />
        <path d="M10 9v4.5M10 6.2v.2" />
    </svg>
);

const normalizeNpi = (v?: string) => (v || '').replace(/\D/g, '').slice(0, 10);

type TRole = 'owner' | 'member' | 'solo';
type TBlocking = { carrier: string; state: string; kind: 'commercial' | 'government' };

type Props = {
    open: boolean;
    // blocked = 具体 check 触发（末步 Run check）；review = 横幅进入（仅 payer 步，末步 Save，无 cost 语境）
    mode: 'blocked' | 'review';
    role: TRole;
    // blocked 模式：这次 check 卡住的 carrier+state+kind。2026-09-15 拍板去掉 "· this check"
    // 单格闸后只用于决定 payer 步是否出现——不再标格子、不再插缺行、不再按格 gate
    blocking?: TBlocking;
    // 本次 check 归属的 provider：practice 视图传归属 provider（override 口径），缺省 = self
    // （my/solo 视图）。仅用于 chip 锚定
    blockingProviderId?: number;
    // blocked 模式下是否含 NPI 步（billing NPI 未设置；review 模式恒不含——能到 payer gap 说明 NPI 已清）
    needNpi: boolean;
    profileNpi?: { npi: string; name: string };
    // 替换 providers 里 self 条目的显示名
    selfName?: string;
    // review 模式锚点：team request 横幅传通知里的 providerId；缺省锚定 self（payer gap / blocked）
    reviewAnchorProviderId?: number;
    // 有未读 GROUP_MEMBER_PAYER_ID_SET_UP 通知的 provider（chip 圆点；保存后后端自动置读）
    pendingProviderIds?: number[];
    onCancel: () => void;
    // blocked：保存完成、全部行已填齐 → 调用方跑 check；review：保存关闭 → 调用方刷新
    onComplete: () => void;
};

type TStep = 'npi' | 'payer';

// 原型 BILLING-NPI MODAL：两个不同生命周期的问题分两步（NPI 一次性、payer ID 每个新 carrier 一次），
// 已答过的步骤跳过。payer grid 数据是 provider 级（挂各自 service template），点主按钮才提交。
const NpiSetupModal: React.FC<Props> = ({
    open,
    mode,
    role,
    blocking,
    blockingProviderId,
    needNpi,
    profileNpi,
    selfName,
    reviewAnchorProviderId,
    pendingProviderIds,
    onCancel,
    onComplete,
}) => {
    const [stepIdx, setStepIdx] = React.useState(0);
    const steps: TStep[] = React.useMemo(() => {
        if (mode === 'review') return ['payer'];
        const list: TStep[] = [];
        if (needNpi) list.push('npi');
        if (blocking) list.push('payer');
        return list.length ? list : ['payer'];
    }, [mode, needNpi, blocking]);
    const step = steps[Math.min(stepIdx, steps.length - 1)];
    const isLast = stepIdx >= steps.length - 1;

    // ── NPI 步：不预选（这么重的默认值会被不加阅读地接受）──
    const [npiSrc, setNpiSrc] = React.useState<'onfile' | 'other' | null>(null);
    // 输入框保持普通受控 Input（布局不动），号码同步写进局部 Form——查询/结果复用 insurance billing
    // onboarding 的 NpiRegistryBox（Form 绑定：10 位触发 NPPES 查询，npiType/taxonomyOptions/
    // taxonomy/npiChecking 写回 ['billing'] 命名空间），表单只当数据桥
    const [npiInput, setNpiInput] = React.useState('');
    const [npiForm] = Form.useForm();
    const npiType = Form.useWatch(['billing', 'npiType'], npiForm);
    const npiTaxonomy = Form.useWatch(['billing', 'taxonomy'], npiForm);
    const npiChecking = Form.useWatch(['billing', 'npiChecking'], npiForm);
    const npiTaxonomyOptions = Form.useWatch(['billing', 'taxonomyOptions'], npiForm);

    // ── payer 步 ──
    const [providers, setProviders] = React.useState<TPracticeProvider[]>([]);
    const [entries, setEntries] = React.useState<Record<number, TPayerIdEntry[]>>({});
    // -1 = 无匹配（回退显示全部）；owner 的 chip 是真过滤不是滚动
    const [filterProvider, setFilterProvider] = React.useState(-1);
    // state 卡默认全展开（记「收起的州」，初始为空），点头部箭头可折叠
    const [closedStates, setClosedStates] = React.useState<string[]>([]);
    // 打开弹窗后 payer-config + insurance 目录的加载中态（payer 步显示 loading，期间主按钮禁用）
    const [setupLoading, setSetupLoading] = React.useState(false);
    // plan 选项全集：insurance 目录（listByState，"Manage insurance carriers and plans" 同源），
    // 按 `${state}|${carrier}` 取该 carrier 的全部 plan；勾选态来自 payer-config 的 plans
    const [planDirectory, setPlanDirectory] = React.useState<Record<string, string[]>>({});
    // Hide/Show plans 折叠态（原型 .pid-carrier expanded，默认展开）
    const [collapsedCarriers, setCollapsedCarriers] = React.useState<Record<string, boolean>>({});

    // 点主按钮才提交（2026-09-08 拍板，覆盖原型的 save-on-change）：编辑只改本地 state 并记 dirty，
    // 主按钮把每个 dirty provider 的完整列表全量 POST 3.2；Cancel/X 直接放弃未提交改动。
    // entriesRef 在 setState updater 里同步更新，保证提交时拿到的是最新整份列表
    const entriesRef = React.useRef<Record<number, TPayerIdEntry[]>>({});
    const dirtyProvidersRef = React.useRef<Set<number>>(new Set());

    React.useEffect(() => {
        if (!open) return;
        setStepIdx(0);
        setNpiSrc(null);
        setNpiInput('');
        npiForm.resetFields();
        setCollapsedCarriers({});
        setSetupLoading(true);
        (async () => {
            // 目录失败不阻塞设置流：回退到只展示 payer-config 里已勾选的 plans（见 renderCarrierCard）
            const [setup, dirRes] = await Promise.all([getPayerIdSetup(), getAllInsuranceList().catch(() => null)]);
            const dir: Record<string, string[]> = {};
            const dirList: TInsuranceServer[] = dirRes?.data?.data || [];
            dirList.forEach((it) => {
                dir[`${it.state}|${it.carrier}`] = it.plans || [];
            });
            setPlanDirectory(dir);
            const selfId = setup.providers.find((p) => p.self)?.providerId ?? 0;
            // 本次 check 归属者：practice 视图是归属 provider（blockingProviderId），my/solo 是 self。
            // 只做 chip 锚定——不再往骨架里插 blocking 缺行（2026-09-15 拍板：grid 就是 payer-config
            // 原样，precheck 猜的 state 不强加给用户）
            const gapOwnerId =
                blockingProviderId != null && setup.providers.some((p) => p.providerId === blockingProviderId)
                    ? blockingProviderId
                    : selfId;
            // payer-config 会带回 insurances 为空的 provider（service template 没配保险）：
            // 没有格子可填就不进 setup（chip/grid 都不出现）
            const visibleProviders = setup.providers.filter((p) => (setup.entries[p.providerId] || []).length > 0);
            setProviders(visibleProviders);
            setEntries(setup.entries);
            entriesRef.current = setup.entries;
            dirtyProvidersRef.current = new Set();
            // 锚定：review 模式 team request 入口传通知里的 providerId（两人共享一州时不能靠州猜人）；
            // 其余（payer gap 的 review、blocked）锚定本次 check 归属者
            let anchorProviderId = gapOwnerId;
            if (
                mode === 'review' &&
                reviewAnchorProviderId != null &&
                visibleProviders.some((p) => p.providerId === reviewAnchorProviderId)
            ) {
                anchorProviderId = reviewAnchorProviderId;
            }
            // 锚定对象被过滤掉时 idx = -1 → 回退显示全部
            const idx = visibleProviders.findIndex((p) => p.providerId === anchorProviderId);
            setFilterProvider(idx);
            setClosedStates([]);
        })().finally(() => setSetupLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mode, blocking, blockingProviderId, role, reviewAnchorProviderId]);

    // 号码写进表单即触发 NpiRegistryBox 的查询链路（10 位查 NPPES；profile NPI 本身不合法时
    // 它渲染 "NPI must be 10 digits." 红错误，而不是无提示地卡住 Next）
    const setNpi = (v: string) => {
        setNpiInput(v);
        npiForm.setFieldValue(['billing', 'npi'], v);
    };

    const pickSrc = (src: 'onfile' | 'other') => {
        setNpiSrc(src);
        setNpi(src === 'onfile' ? profileNpi?.npi || '' : '');
    };

    const activeProvider = filterProvider > -1 ? providers[filterProvider] : undefined;
    const shownStates = React.useMemo(() => {
        return activeProvider ? activeProvider.states.slice() : Array.from(new Set(providers.flatMap((p) => p.states)));
    }, [activeProvider, providers]);

    const entriesOf = (providerId: number) => entries[providerId] || [];
    const activeEntries = entriesOf(activeProvider?.providerId ?? providers.find((p) => p.self)?.providerId ?? 0);

    // 提交全部 dirty provider（每个都是完整列表全量替换）；成功后清 dirty
    const saveDirty = React.useCallback(async () => {
        const pending = Array.from(dirtyProvidersRef.current);
        dirtyProvidersRef.current = new Set();
        await Promise.all(pending.map((pid) => savePayerEntries(pid, entriesRef.current[pid] || [])));
    }, []);

    const updateEntry = (providerId: number, next: TPayerIdEntry) => {
        setEntries((prev) => {
            const list = (prev[providerId] || []).slice();
            const i = list.findIndex((e) => e.carrier === next.carrier && e.state === next.state);
            if (i >= 0) list[i] = next;
            else list.push(next);
            const merged = { ...prev, [providerId]: list };
            entriesRef.current = merged;
            return merged;
        });
        dirtyProvidersRef.current.add(providerId);
    };

    // Run check 门槛（2026-09-15 拍板，替代原型的 this-check 单格闸）：grid 里所有 provider 的
    // 每一行 carrier 都至少填了一个 payer ID（commercial 或 government 任一）。
    // 2026-09-28 改：门槛不再体现为禁用按钮，而是点击后的提示——禁用态说不出缺的是哪一位，
    // owner 的 chip 又是真过滤，没填的那位很可能正被另一张 chip 挡着，看着满屏填好的格子无从下手。
    // review 末步是 Save（局部编辑本就允许留空），不参与这道判定
    const missingProviders = React.useMemo(() => {
        if (mode === 'review') return [];
        return providers.filter((p) =>
            (entries[p.providerId] || []).some((e) => !e.commercialPayerId && !e.governmentPayerId),
        );
    }, [mode, providers, entries]);

    const providerName = (p: TPracticeProvider) => (p.self ? selfName || p.name : p.name);

    // 原型 renderNpi 的门槛：registry 命中且 taxonomy 已选（有可选项时；NPPES 记录几乎必带 taxonomy）；
    // 查询进行中（含防抖等待）也拦住，避免结果未落就提交
    const npiReady =
        !!npiType &&
        npiType !== 'notfound' &&
        !npiChecking &&
        ((npiTaxonomyOptions || []).length === 0 || !!npiTaxonomy);

    // /setup 的 providerPayers 载荷：全部 provider 的完整条目快照（只带填了任一 payer ID 的行，
    // 空骨架行不携带信息）
    const toProviderPayers = () =>
        providers
            .map((p) => ({
                providerId: p.providerId,
                payers: (entriesRef.current[p.providerId] || [])
                    .filter((e) => e.commercialPayerId || e.governmentPayerId)
                    .map((e) => ({
                        state: e.state,
                        carrier: e.carrier,
                        commercialPayerId: e.commercialPayerId || undefined,
                        governmentPayerId: e.governmentPayerId || undefined,
                    })),
            }))
            .filter((p) => p.payers.length > 0);

    // 主按钮 = 唯一的提交点。保存路由（2026-09-09 拍板）：
    // - setup 流（NPI 步出现过的首次设置）→ 2.1 /setup 一次带上 billingNpi + 全部 payer 映射（顺带开 org 开关）；
    // - 单独进入 payer 步（review / blocked 但 NPI 已设）→ 3.2 payer-config 按 dirty provider 全量提交
    const primary = async () => {
        if (step === 'npi') {
            if (!isLast) {
                setStepIdx((i) => i + 1);
                return;
            }
            // steps = ['npi'] 的独步流程：payer 数据未经编辑，快照原样随 setup 上送
            await saveEligibilitySetup({
                billingNpi: String(npiForm.getFieldValue(['billing', 'npi']) || ''),
                billingTaxonomyCode: npiForm.getFieldValue(['billing', 'taxonomy']) || undefined,
                providerPayers: toProviderPayers(),
            });
            onComplete();
            return;
        }
        // payer 末步
        if (mode === 'blocked' && missingProviders.length) {
            // 点名只在 owner 的多人 grid 里有意义：solo/member 屏幕上只有自己，报名字是噪音
            message.warning(
                role === 'owner' && providers.length > 1
                    ? C.blockedToastNamed(missingProviders.map(providerName))
                    : C.blockedToast,
            );
            return;
        }
        if (mode === 'blocked' && needNpi) {
            dirtyProvidersRef.current = new Set();
            await saveEligibilitySetup({
                billingNpi: String(npiForm.getFieldValue(['billing', 'npi']) || ''),
                billingTaxonomyCode: npiForm.getFieldValue(['billing', 'taxonomy']) || undefined,
                providerPayers: toProviderPayers(),
            });
        } else {
            await saveDirty();
        }
        onComplete();
    };

    const back = () => {
        if (stepIdx === 0) onCancel();
        else setStepIdx((i) => i - 1);
    };

    // payer 步只在加载中禁用：providers 未落地时缺没缺无从判断，放行只会提示一个不存在的缺口
    const primaryDisabled = step === 'npi' ? !npiReady : setupLoading;
    const primaryLabel = (() => {
        if (!isLast) return C.next;
        return mode === 'review' ? C.save : C.runCheck;
    })();

    const renderNpiStep = () => (
        <>
            <p className={s.npiIntro}>{C.npiIntro}</p>
            {role === 'owner' && (
                <div className={s.ownerNote}>
                    <InfoIcon />
                    <span className={s.noteBody}>{C.ownerScope}</span>
                </div>
            )}
            <div className={s.radioCards}>
                <button
                    type="button"
                    className={`${s.radioCard} ${npiSrc === 'onfile' ? s.radioCardOn : ''}`}
                    onClick={() => pickSrc('onfile')}
                >
                    <span className={s.radioDot} />
                    <span>
                        <span className={s.radioTitle}>{C.onFileTitle}</span>
                        <span className={s.radioDesc}>
                            <span className={s.npiNum}>{profileNpi?.npi || '—'}</span>
                            {profileNpi?.name ? ` · ${profileNpi.name}` : ''}
                            {role === 'owner' ? C.onFileOwnerNote : ''}
                        </span>
                    </span>
                </button>
                <button
                    type="button"
                    className={`${s.radioCard} ${npiSrc === 'other' ? s.radioCardOn : ''}`}
                    onClick={() => pickSrc('other')}
                >
                    <span className={s.radioDot} />
                    <span>
                        <span className={s.radioTitle}>
                            {role === 'owner' ? C.otherTitleOwner : C.otherTitle}
                            {role === 'owner' && <span className={s.recBadge}>{C.recommended}</span>}
                        </span>
                        <span className={s.radioDesc}>{role === 'owner' ? C.otherDescOwner : C.otherDesc}</span>
                    </span>
                </button>
            </div>
            {npiSrc === 'other' && (
                <div className={s.npiField}>
                    <label htmlFor="eligNpiInput">
                        {C.npiLabel}
                        <InfoTip title={C.npiTip} />
                    </label>
                    <Input
                        id="eligNpiInput"
                        value={npiInput}
                        maxLength={10}
                        inputMode="numeric"
                        placeholder={C.npiPlaceholder}
                        onChange={(e) => setNpi(normalizeNpi(e.target.value))}
                    />
                </div>
            )}
            {/* NpiRegistryBox 是 Form 绑定组件（查询/结果都走 ['billing'] 命名空间的表单字段），
                这里包一层局部 Form 只当数据桥复用它；component=false 不产生嵌套 <form>。
                输入框是上面的普通受控 Input（布局不动），npi 靠隐藏字段保持注册——
                useWatch 只见已注册字段，否则组件的查询 effect 看不到 setNpi 写入的号码 */}
            <div className={s.npiFormScope}>
                <Form form={npiForm} layout="vertical" component={false}>
                    <Form.Item name={['billing', 'npi']} noStyle>
                        <HiddenFormField />
                    </Form.Item>
                    {/* eligibility 走 §3.5 的独立 NPI 端点：本弹窗对没开通 Insurance Billing 的
                        practice 同样要能用，onboarding 那条会校验 owner + api key */}
                    {npiSrc && <NpiRegistryBox ns={['billing']} lookup={lookupNpiStandalone} />}
                </Form>
            </div>
        </>
    );

    const renderCarrierCard = (providerId: number, entry: TPayerIdEntry) => {
        const carrierKey = `${providerId}|${entry.state}|${entry.carrier}`;
        const collapsed = !!collapsedCarriers[carrierKey];
        // 选项全集来自 insurance 目录（该 state+carrier 的全部 plan）；payer-config 里目录外的值
        // （含脏数据）追加为已勾选项照常显示——不掩盖数据问题。目录没拉到时退化为只显示已勾选的
        const dirPlans = planDirectory[`${entry.state}|${entry.carrier}`] || [];
        const planOptions = [...dirPlans, ...entry.plans.filter((p) => !dirPlans.includes(p))];
        const selectedCount = planOptions.filter((p) => entry.plans.includes(p)).length;
        return (
            <div className={s.pidCarrier} key={`${entry.state}-${entry.carrier}`}>
                <div className={s.pidCarrierHead}>
                    <span className={s.pidCarrierName}>{entry.carrier}</span>
                    {/* 原型 .pid-hide：折叠整个 carrier body（含 payer ID 输入），默认展开 */}
                    <button
                        type="button"
                        className={s.pidHide}
                        onClick={() => setCollapsedCarriers((prev) => ({ ...prev, [carrierKey]: !prev[carrierKey] }))}
                    >
                        {collapsed ? C.showPlans : C.hidePlans}
                    </button>
                </div>
                {!collapsed && (
                    <div className={s.pidCarrierBody}>
                        {/* plan 只读展示（2026-09-08 拍板）：数据来自 insurance survey（3.1 下发），
                        本弹窗不提供修改入口——checkbox 禁用、无 Select all/Reset，
                        payer ID 才是这里要收集的东西 */}
                        <div
                            className={s.pidCount}
                        >{`${selectedCount} plan${selectedCount === 1 ? '' : 's'} selected`}</div>
                        <div className={s.pidPlansHead}>
                            <b>{C.plansSelectHead}</b>
                        </div>
                        <div className={s.pidPlansGrid}>
                            {planOptions.map((p) => (
                                <Checkbox key={p} checked={entry.plans.includes(p)} disabled>
                                    {p}
                                </Checkbox>
                            ))}
                        </div>
                        <div className={s.pidIdsHead}>
                            <b>{C.idsHead}</b>
                            <div className={s.fieldHelp}>{C.idsHelp}</div>
                        </div>
                        <div className={s.pidIdsRow}>
                            <div>
                                <label>{C.commercialLabel}</label>
                                <Input
                                    value={entry.commercialPayerId || ''}
                                    maxLength={12}
                                    placeholder={C.idPlaceholder}
                                    onChange={(e) =>
                                        updateEntry(providerId, { ...entry, commercialPayerId: e.target.value.trim() })
                                    }
                                />
                            </div>
                            <div>
                                <label>{C.governmentLabel}</label>
                                <Input
                                    value={entry.governmentPayerId || ''}
                                    maxLength={12}
                                    placeholder={C.idPlaceholder}
                                    onChange={(e) =>
                                        updateEntry(providerId, { ...entry, governmentPayerId: e.target.value.trim() })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderPayerStep = () => {
        // grid 数据（payer-config + 目录）未落地：占位 loading，避免闪一屏空 grid
        if (setupLoading) {
            return (
                <div className={s.pidLoading}>
                    <Spin />
                </div>
            );
        }
        const providerId = activeProvider?.providerId ?? providers.find((p) => p.self)?.providerId ?? 0;
        const editingOther = !!activeProvider && !activeProvider.self;
        return (
            <>
                <p className={s.npiIntro}>
                    {C.payerIntro}
                    {editingOther ? ` ${C.payerIntroOwner}` : ''}
                </p>
                {role === 'owner' && providers.length > 1 && (
                    <div className={s.providerChips}>
                        {providers.map((p, i) => {
                            const waiting = !!pendingProviderIds?.includes(p.providerId);
                            return (
                                <button
                                    type="button"
                                    key={p.providerId}
                                    className={`${s.providerChip} ${i === filterProvider ? s.providerChipOn : ''}`}
                                    onClick={() => setFilterProvider(i)}
                                >
                                    {p.self ? selfName || p.name : p.name}
                                    {waiting && <span className={s.chipDot} />}
                                </button>
                            );
                        })}
                    </div>
                )}
                {/* state 卡片默认全展开（原型行为），点头部箭头可收起/展开 */}
                {shownStates.map((state) => {
                    const stateEntries = activeEntries.filter((e) => e.state === state);
                    const opened = !closedStates.includes(state);
                    const toggle = () =>
                        setClosedStates((prev) =>
                            prev.includes(state) ? prev.filter((x) => x !== state) : [...prev, state],
                        );
                    return (
                        <div key={state} className={`${s.pidState} ${opened ? s.pidStateOpen : ''}`}>
                            <div
                                className={s.pidStateHead}
                                role="button"
                                tabIndex={0}
                                onClick={toggle}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') toggle();
                                }}
                            >
                                {state}
                                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="6 8 10 12 14 8" />
                                </svg>
                            </div>
                            <div className={s.pidStateBody}>
                                {stateEntries.map((entry) => renderCarrierCard(providerId, entry))}
                            </div>
                        </div>
                    );
                })}
            </>
        );
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            title={step === 'npi' ? C.npiTitle : C.payerTitle}
            width={560}
            wrapClassName={s.eligModalScope}
            footer={
                <div className={s.costFooterBtns}>
                    <Button onClick={back}>{stepIdx === 0 ? C.cancel : C.back}</Button>
                    <Button type="primary" disabled={primaryDisabled} onClick={primary}>
                        {primaryLabel}
                    </Button>
                </div>
            }
        >
            {step === 'npi' ? renderNpiStep() : renderPayerStep()}
        </Modal>
    );
};

export default NpiSetupModal;
